import { useState, useEffect, useCallback, useRef } from 'react';
import { Typography, Box, Divider, Button } from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { LoadingButton } from '@mui/lab';

import ChartContentsEditor from './ChartContentsEditor';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { useApi } from '@/providers/ApiContext';
import { getChartSections } from '@/actions/chartSection';
import { getChartElements } from '@/actions/chartElement';
import {
  createChartSection,
  updateChartSection,
  deleteChartSection,
} from '@/actions/chartSection';
import {
  createChartElement,
  updateChartElement,
  deleteChartElement,
} from '@/actions/chartElement';
import { calculateChanges } from '@/util/chartLexicalConverter';
import type { ChartSection, ChartElement, ChartWaypoint, EntityId, ElementsBySection } from '@/types';

interface ChartContentsProps {
  chartId: EntityId;
  chartStatus: string;
  waypoints: ChartWaypoint[];
}

function ChartContents({ chartId, chartStatus, waypoints }: ChartContentsProps): React.JSX.Element {
  const api = useApi();
  const [sections, setSections] = useState<ChartSection[]>([]);
  const [elementsBySection, setElementsBySection] = useState<ElementsBySection>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [localSections, setLocalSections] = useState<ChartSection[]>([]);
  const [localElements, setLocalElements] = useState<ElementsBySection>({});
  const headerRef = useRef<HTMLDivElement>(null);

  const isEditable =
    chartStatus === 'draft' ||
    chartStatus === 'pending' ||
    chartStatus === 'active';
  const editMode = chartStatus === 'active' ? 'prompt-only' : 'full';

  const fetchSectionsAndElements = useCallback(async (): Promise<void> => {
    if (!chartId || !api) return;

    try {
      setLoading(true);
      setApiError(null);

      // Fetch all sections
      const sectionsResponse = await getChartSections(api, chartId);
      const sectionsList = sectionsResponse.results || [];
      setSections(sectionsList);

      // Fetch elements for each section
      const elementsMap: ElementsBySection = {};
      for (const section of sectionsList) {
        if (section.entity_id) {
          try {
            const elementsResponse = await getChartElements(
              api,
              chartId,
              section.entity_id,
            );
            elementsMap[section.entity_id] = elementsResponse.results || [];
          } catch (err) {
            console.error(
              'Error fetching elements for section:',
              section.entity_id,
              err,
            );
            elementsMap[section.entity_id] = [];
          }
        }
      }
      setElementsBySection(elementsMap);

      // Initialize local state
      setLocalSections([...sectionsList]);
      setLocalElements({ ...elementsMap });
      setIsDirty(false);
    } catch (err) {
      console.error('Error fetching chart sections:', err);
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to load chart contents. This could be due to network issues, server problems, or the chart may not exist.',
      );
    } finally {
      setLoading(false);
    }
  }, [api, chartId]);

  useEffect(() => {
    fetchSectionsAndElements();
  }, [fetchSectionsAndElements]);

  const handleSectionsChange = useCallback((newSections: ChartSection[]): void => {
    setLocalSections(newSections);
    setIsDirty(true);
  }, []);

  const handleElementsChange = useCallback((sectionId: string, newElements: ChartElement[]): void => {
    setLocalElements((prev) => ({
      ...prev,
      [sectionId]: newElements,
    }));
    setIsDirty(true);
  }, []);

  const handleCancel = useCallback(async (): Promise<void> => {
    if (!isDirty) return;

    try {
      setSaving(true);
      setApiError(null);
      // Discard local changes and refresh from server
      await fetchSectionsAndElements();
    } catch (err) {
      console.error('Error canceling changes:', err);
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to refresh chart contents. This could be due to network issues or server problems.',
      );
    } finally {
      setSaving(false);
    }
  }, [isDirty, fetchSectionsAndElements]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!isDirty || !api || !chartId) return;

    try {
      setSaving(true);
      setApiError(null);

      // In prompt-only mode, only save prompt changes for elements
      if (editMode === 'prompt-only') {
        // Only update elements with prompt changes
        const elementsToUpdate: Array<ChartElement & { section_id: string }> = [];
        Object.entries(localElements).forEach(([sectionKey, elements]) => {
          elements.forEach((element) => {
            if (element.entity_id) {
              // Find the original element
              const originalElement = Object.values(elementsBySection)
                .flat()
                .find((el) => el.entity_id === element.entity_id);
              // Only update if prompt changed
              if (
                originalElement &&
                originalElement.prompt !== element.prompt
              ) {
                elementsToUpdate.push({
                  ...element,
                  section_id: sectionKey.startsWith('temp-')
                    ? ''
                    : sectionKey,
                } as ChartElement & { section_id: string });
              }
            }
          });
        });

        // Update only the prompt field for changed elements
        for (const element of elementsToUpdate) {
          if (element.section_id && element.entity_id) {
            await updateChartElement(
              api,
              chartId,
              element.section_id,
              element.entity_id,
              {
                prompt: element.prompt,
              },
            );
          }
        }

        // Refresh data to get updated timestamps
        await fetchSectionsAndElements();
        return;
      }

      // Flatten local elements and preserve section relationship
      const allLocalElements: Array<ChartElement & { section_id: string }> = [];
      Object.entries(localElements).forEach(([sectionKey, elements]) => {
        elements.forEach((element) => {
          // If element doesn't have a section_id, try to derive it from the sectionKey
          const elementWithSection: ChartElement & { section_id: string } = {
            ...element,
            section_id: element.section_id || sectionKey,
          };
          allLocalElements.push(elementWithSection);
        });
      });

      // Validate elements
      const validationErrors: string[] = [];

      allLocalElements.forEach((element) => {
        if (element.type === 'narrative' || element.type === 'table') {
          // Check waypoints
          const elementWaypoints = element.waypoints || [];
          if (elementWaypoints.length === 0) {
            validationErrors.push(
              'Narrative and table elements must have at least one waypoint selected.',
            );
          }
          // Check description
          if (!element.description || element.description.trim() === '') {
            validationErrors.push(
              'Narrative and table elements must have a description.',
            );
          }
          // Check prompt
          if (!element.prompt || element.prompt.trim() === '') {
            validationErrors.push(
              'Narrative and table elements must have a prompt.',
            );
          }
        } else if (element.type === 'static') {
          // Check description (content) for static elements
          if (!element.description || element.description.trim() === '') {
            validationErrors.push('Static elements must have content.');
          }
        }
      });

      if (validationErrors.length > 0) {
        setApiError(`Cannot save: ${validationErrors[0]}`);
        setSaving(false);
        return;
      }

      // Calculate changes
      const allOriginalElements = Object.values(elementsBySection).flat();
      const changes = calculateChanges(
        sections,
        allOriginalElements,
        localSections,
        allLocalElements,
      );

      // Helper function to convert waypoints to entity_ids
      const convertWaypointsToEntityIds = (waypoints: (string | ChartWaypoint)[]): string[] => {
        if (!waypoints || waypoints.length === 0) return [];
        return waypoints.map((wp) =>
          typeof wp === 'string' ? wp : wp.entity_id,
        );
      };

      // Execute CRUD operations and update local state optimistically
      // First, create new sections and build a mapping from temporary IDs to real entity_ids
      const tempIdToEntityIdMap = new Map<string, string>();
      const createdSectionsMap = new Map<ChartSection, ChartSection>();
      // Sort sections to create to match their order
      const sectionsToCreateSorted = [...changes.sectionsToCreate].sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );
      for (const section of sectionsToCreateSorted) {
        const createdSection = await createChartSection(api, chartId, {
          heading: section.heading,
          style: section.style,
          number: section.number,
          order: section.order,
        });
        // Map temporary ID to real entity_id - use order to create temp key
        const tempId = `temp-${section.order}`;
        if (createdSection && createdSection.entity_id) {
          tempIdToEntityIdMap.set(tempId, createdSection.entity_id);
          createdSectionsMap.set(section, {
            ...section,
            entity_id: createdSection.entity_id,
            created_at: createdSection.created_at,
            updated_at: createdSection.updated_at,
          });
        }
      }

      // Update existing sections
      for (const section of changes.sectionsToUpdate) {
        if (!section.entity_id) continue;
        const updatedSection = await updateChartSection(
          api,
          chartId,
          section.entity_id,
          {
            heading: section.heading,
            style: section.style,
            number: section.number,
            order: section.order,
          },
        );
        // Store updated section with server response
        createdSectionsMap.set(section, {
          ...section,
          updated_at: updatedSection.updated_at,
        });
      }

      // Delete removed sections (this will cascade delete elements)
      for (const section of changes.sectionsToDelete) {
        if (section.entity_id) {
          await deleteChartSection(api, chartId, section.entity_id);
        }
      }

      // Create new elements - map temporary section IDs to real entity_ids
      const createdElementsMap = new Map<ChartElement, ChartElement>();
      for (const element of changes.elementsToCreate) {
        let sectionId = element.section_id || '';
        // If section_id is a temporary ID, map it to the real entity_id
        if (sectionId && sectionId.startsWith('temp-')) {
          sectionId = tempIdToEntityIdMap.get(sectionId) || sectionId;
        }
        if (sectionId) {
          const createdElement = await createChartElement(
            api,
            chartId,
            sectionId,
            {
              section_id: sectionId,
              type: element.type,
              description: element.description,
              prompt: element.prompt,
              for_each: element.for_each,
              waypoints: convertWaypointsToEntityIds(element.waypoints || []),
              metadata: element.metadata,
              order: element.order,
            },
          );
          if (createdElement && createdElement.entity_id) {
            createdElementsMap.set(element, {
              ...element,
              entity_id: createdElement.entity_id,
              section_id: sectionId,
              created_at: createdElement.created_at,
              updated_at: createdElement.updated_at,
              waypoints: createdElement.waypoints || element.waypoints || [],
            });
          }
        } else {
          console.warn(
            'Could not find section_id for element creation:',
            element,
          );
        }
      }

      // Update existing elements
      for (const element of changes.elementsToUpdate) {
        const sectionId = element.section_id || '';
        if (sectionId && element.entity_id) {
          const updatedElement = await updateChartElement(
            api,
            chartId,
            sectionId,
            element.entity_id,
            {
              type: element.type,
              description: element.description,
              prompt: element.prompt,
              for_each: element.for_each,
              waypoints: convertWaypointsToEntityIds(element.waypoints || []),
              metadata: element.metadata,
              order: element.order,
            },
          );
          // Store updated element with server response
          createdElementsMap.set(element, {
            ...element,
            updated_at: updatedElement.updated_at,
            waypoints: updatedElement.waypoints || element.waypoints || [],
          });
        }
      }

      // Delete removed elements
      for (const element of changes.elementsToDelete) {
        const sectionId = element.section_id || '';
        if (sectionId && element.entity_id) {
          await deleteChartElement(
            api,
            chartId,
            sectionId,
            element.entity_id,
          );
        }
      }

      // Update local state optimistically instead of refetching
      // Update sections
      const updatedSections = localSections
        .map((section) => {
          // Check if this section was created
          const createdSection = createdSectionsMap.get(section);
          if (createdSection) {
            return createdSection;
          }
          // Check if this section was updated
          const updatedSection = changes.sectionsToUpdate.find(
            (s) =>
              s.entity_id &&
              section.entity_id &&
              s.entity_id === section.entity_id,
          );
          if (updatedSection) {
            const serverSection = createdSectionsMap.get(updatedSection);
            return serverSection || { ...section, ...updatedSection };
          }
          // Check if this section should be deleted
          const shouldDelete = changes.sectionsToDelete.some(
            (s) =>
              s.entity_id &&
              section.entity_id &&
              s.entity_id === section.entity_id,
          );
          if (shouldDelete) {
            return null; // Mark for removal
          }
          return section;
        })
        .filter((section): section is ChartSection => section !== null);

      // Add newly created sections that weren't in localSections
      changes.sectionsToCreate.forEach((section) => {
        const createdSection = createdSectionsMap.get(section);
        if (
          createdSection &&
          !updatedSections.find((s) => s.entity_id === createdSection.entity_id)
        ) {
          updatedSections.push(createdSection);
        }
      });

      // Update elements
      const updatedElementsBySection: ElementsBySection = { ...localElements };

      // Remove elements that were deleted
      changes.elementsToDelete.forEach((element) => {
        const sectionKey = element.section_id || '';
        if (sectionKey && updatedElementsBySection[sectionKey]) {
          updatedElementsBySection[sectionKey] = updatedElementsBySection[
            sectionKey
          ].filter(
            (el) =>
              !(
                el.entity_id &&
                element.entity_id &&
                el.entity_id === element.entity_id
              ),
          );
        }
      });

      // Update or add elements
      Object.entries(localElements).forEach(([sectionKey, elements]) => {
        const updatedElements = elements.map((element) => {
          // Check if this element was created
          const createdElement = createdElementsMap.get(element);
          if (createdElement) {
            return createdElement;
          }
          // Check if this element was updated
          const updatedElement = changes.elementsToUpdate.find(
            (el) =>
              el.entity_id &&
              element.entity_id &&
              el.entity_id === element.entity_id,
          );
          if (updatedElement) {
            const serverElement = createdElementsMap.get(updatedElement);
            return serverElement || { ...element, ...updatedElement };
          }
          return element;
        });

        // Add newly created elements for this section
        changes.elementsToCreate.forEach((element) => {
          const createdElement = createdElementsMap.get(element);
          if (createdElement) {
            // Map section_id to sectionKey (could be temp ID or real entity_id)
            let targetSectionKey = element.section_id || '';
            if (targetSectionKey && targetSectionKey.startsWith('temp-')) {
              targetSectionKey =
                tempIdToEntityIdMap.get(targetSectionKey) || targetSectionKey;
            }
            if (
              targetSectionKey === sectionKey ||
              createdElement.section_id === sectionKey
            ) {
              // Check if element already exists
              if (
                !updatedElements.find(
                  (el) => el.entity_id === createdElement.entity_id,
                )
              ) {
                updatedElements.push(createdElement);
              }
            }
          }
        });

        updatedElementsBySection[sectionKey] = updatedElements;
      });

      // Handle elements for newly created sections (they might be in temp keys)
      changes.elementsToCreate.forEach((element) => {
        const createdElement = createdElementsMap.get(element);
        if (createdElement) {
          const targetSectionKey = createdElement.section_id || '';
          if (targetSectionKey && !updatedElementsBySection[targetSectionKey]) {
            updatedElementsBySection[targetSectionKey] = [];
          }
          if (targetSectionKey && updatedElementsBySection[targetSectionKey]) {
            if (
              !updatedElementsBySection[targetSectionKey].find(
                (el) => el.entity_id === createdElement.entity_id,
              )
            ) {
              updatedElementsBySection[targetSectionKey].push(createdElement);
            }
          }
        }
      });

      // Remove temp keys that have been mapped to real entity_ids
      Object.keys(updatedElementsBySection).forEach((key) => {
        if (key.startsWith('temp-')) {
          const realEntityId = tempIdToEntityIdMap.get(key);
          if (realEntityId && updatedElementsBySection[realEntityId]) {
            // Merge elements from temp key to real key
            updatedElementsBySection[realEntityId] = [
              ...updatedElementsBySection[realEntityId],
              ...updatedElementsBySection[key],
            ];
            delete updatedElementsBySection[key];
          } else if (realEntityId) {
            // Move elements from temp key to real key
            updatedElementsBySection[realEntityId] =
              updatedElementsBySection[key];
            delete updatedElementsBySection[key];
          }
        }
      });

      // Update state with optimized data
      setSections(updatedSections);
      setElementsBySection(updatedElementsBySection);
      setLocalSections(updatedSections);
      setLocalElements(updatedElementsBySection);
      setIsDirty(false);
    } catch (err) {
      console.error('Error saving chart contents:', err);
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to save chart contents. This could be due to network issues, server problems, or invalid data.',
      );
    } finally {
      setSaving(false);
    }
  }, [
    api,
    chartId,
    isDirty,
    editMode,
    sections,
    elementsBySection,
    localSections,
    localElements,
    fetchSectionsAndElements,
  ]);

  if (loading) {
    return (
      <Box>
        <Typography
          variant='h6'
          sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
        >
          Chart Contents
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Loading chart contents...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        ref={headerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography
          variant='h6'
          sx={{ fontWeight: 'bold', color: 'primary.main' }}
        >
          Chart Contents
        </Typography>
        {isEditable && editMode === 'full' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              size='small'
              variant='contained'
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty || saving}
            >
              Save
            </LoadingButton>
            <Button
              size='small'
              variant='outlined'
              onClick={handleCancel}
              disabled={!isDirty || saving}
            >
              Cancel
            </Button>
          </Box>
        )}
        {isEditable && editMode === 'prompt-only' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LoadingButton
              size='small'
              variant='contained'
              startIcon={<SaveOutlinedIcon />}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty || saving}
            >
              Save
            </LoadingButton>
            <Button
              size='small'
              variant='outlined'
              onClick={handleCancel}
              disabled={!isDirty || saving}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <ChartContentsEditor
        sections={localSections}
        elementsBySection={localElements}
        onSectionsChange={handleSectionsChange}
        onElementsChange={handleElementsChange}
        isEditable={isEditable}
        editMode={editMode}
        waypoints={waypoints || []}
        headerRef={headerRef}
      />
      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Box>
  );
}

export default ChartContents;

